import { Link } from "react-router";
import type { Route } from "./+types/home";
import Shell from "../components/Shell";
import NewsletterSignup from "../components/NewsletterSignup";
import HeroArtGrid from "../components/HeroArtGrid";
import InlineHighlight from "../components/InlineHighlight";
import { listArticles, coverUrl, type ArticleListItem } from "../lib/api";
import { currentSource } from "../lib/attribution";
import { analytics } from "../lib/analytics";
import { faDate } from "../lib/format";
import {
  HERO_ART,
  OG_IMAGE,
  stats,
  topics,
  courses,
  courseUrl,
  faNum,
  faCount,
  faRating,
} from "../lib/faData";

export function meta() {
  return [
    { title: "احسان گازار | مهندس نرم‌افزار" },
    {
      name: "description",
      content:
        "نوشته‌ها و دوره‌های فارسی درباره‌ی طراحی سیستم، مهندسی نرم‌افزار و توسعه‌ی محصولات مبتنی بر هوش مصنوعی.",
    },
    { property: "og:title", content: "احسان گازار | مهندس نرم‌افزار" },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://fa.gazar.dev/" },
    {
      property: "og:description",
      content:
        "نوشته‌ها و دوره‌های فارسی درباره‌ی طراحی سیستم، مهندسی نرم‌افزار و توسعه‌ی محصولات مبتنی بر هوش مصنوعی.",
    },
    { property: "og:image", content: OG_IMAGE },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:image", content: OG_IMAGE },
    { tagName: "link", rel: "canonical", href: "https://fa.gazar.dev/" },
    // Lowercase `hreflang` on purpose, and left alone deliberately. React logs
    // "Invalid DOM property `hreflang`" against it, but that warning is dev-only
    // and the container runs NODE_ENV=production, so it never appears in prod.
    // The camelCase spelling silences the dev warning by serialising the
    // attribute as `hrefLang`, which browsers parse the same way but which is
    // not the spelling the spec or any hreflang validator writes.
    { tagName: "link", rel: "alternate", hreflang: "fa", href: "https://fa.gazar.dev/" },
    { tagName: "link", rel: "alternate", hreflang: "en", href: "https://gazar.dev/" },
    { tagName: "link", rel: "alternate", hreflang: "x-default", href: "https://gazar.dev/" },
  ];
}

export async function loader() {
  // An empty list is a legitimate state, not an error: this page has to render
  // before a single article exists, and it did for several hours on 21 Aug.
  try {
    const { items } = await listArticles(4);
    return { articles: items };
  } catch {
    return { articles: [] as ArticleListItem[] };
  }
}

export default function Home({ loaderData }: Route.ComponentProps) {
  const { articles } = loaderData;
  return (
    <Shell onHome>
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-navy py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="max-w-3xl">
              <p className="mb-5 text-[13px] font-bold tracking-[0.18em] text-gray-50/60">
                مهندس نرم‌افزار Staff · لندن
              </p>
              <h1 className="mb-7 text-[34px] sm:text-[42px] md:text-[48px] font-bold leading-[1.55] text-gray-50">
                <InlineHighlight color="red">نرم‌افزار می‌سازم</InlineHighlight> و از چالش‌های
                روزمره‌ی آن می‌نویسم.
              </h1>
              <p className="mb-8 text-[18px] md:text-[20px] leading-[2.1] text-gray-50/80">
                معماری، مقیاس‌پذیری، کیفیت کد و توسعه‌ی محصولات واقعی با مدل‌های زبانی. اینجا
                تجربه‌هایم را به زبان فارسی و بدون ساده‌سازی‌های گمراه‌کننده به اشتراک می‌گذارم.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/maghale"
                  className="inline-flex items-center px-7 py-3.5 rounded-smooth bg-white text-brand-navy text-[17px] font-bold transition-opacity duration-150 hover:opacity-90"
                >
                  خواندن نوشته‌ها
                </Link>
                <a
                  href="#newsletter"
                  className="inline-flex items-center px-7 py-3.5 rounded-smooth border border-white/25 text-white text-[17px] font-bold transition-colors duration-150 hover:border-white"
                >
                  عضویت در خبرنامه
                </a>
              </div>
              <p className="mt-7 text-[14px] leading-[2] text-gray-50/50">
                بیش از {faNum(15)} سال تجربه · {faNum(120)}+ مهندس منتورشده · {faNum(500)}+ مقاله
                به انگلیسی
              </p>
            </div>
            <HeroArtGrid images={HERO_ART} />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-border-subtle bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-2 gap-y-10 px-4 py-12 sm:px-6 lg:px-8 md:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="mb-2 text-[32px] font-bold text-brand-navy md:text-[38px]">
                {faNum(s.value)}+
              </div>
              <div className="text-[14px] leading-[1.9] text-gray-900/60">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Articles. New in the app version: the single-page original had nowhere
          to put them, which is the whole reason this became its own app. */}
      <section id="articles" className="bg-white py-16 md:py-20 scroll-mt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex items-end justify-between gap-6">
            <h2 className="text-[28px] font-bold leading-[1.6] text-brand-navy md:text-[34px]">
              تازه‌ترین نوشته‌ها
            </h2>
            {articles.length > 0 && (
              <Link to="/maghale" className="shrink-0 text-[15px] font-bold text-brand-navy hover:opacity-70">
                همه‌ی نوشته‌ها
              </Link>
            )}
          </div>
          {articles.length === 0 ? (
            <p className="text-[17px] leading-[2.1] text-gray-900/60">
              هنوز نوشته‌ای منتشر نشده است. برای باخبر شدن از اولین نوشته، در خبرنامه عضو شوید.
            </p>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {articles.map((a) => (
                <Link
                  key={a.id}
                  to={`/maghale/${a.slug}`}
                  className="flex flex-col overflow-hidden rounded-smooth-lg border border-border-subtle bg-gray-50 transition-colors duration-150 hover:border-border-strong"
                >
                  {/* The cover is decoration, so it is skipped entirely when the
                      article has none rather than reserving an empty box. An
                      article published before covers existed on this app still
                      renders as a clean text card. */}
                  {coverUrl(a.image) ? (
                    <img
                      src={coverUrl(a.image) as string}
                      alt=""
                      loading="lazy"
                      width={1200}
                      height={675}
                      className="aspect-video w-full border-b border-border-subtle object-cover"
                    />
                  ) : null}
                  <div className="flex flex-1 flex-col p-6">
                  <h3 className="mb-3 text-[19px] font-bold leading-[1.8] text-brand-navy">
                    {a.title}
                  </h3>
                  {a.description ? (
                    <p className="mb-5 flex-1 text-[16px] leading-[2.1] text-gray-900/70">
                      {a.description}
                    </p>
                  ) : null}
                  <time className="text-[14px] text-gray-900/55">{faDate(a.publishedAt)}</time>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Topics */}
      <section id="topics" className="bg-gray-50 py-16 md:py-20 scroll-mt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="mb-10 text-[28px] font-bold leading-[1.6] text-brand-navy md:text-[34px]">
            درباره‌ی چه چیزهایی می‌نویسم
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            {topics.map((t) => (
              <article key={t.title} className="rounded-smooth-lg border border-border-subtle bg-white p-6">
                <h3 className="mb-3 text-[19px] font-bold text-brand-navy">{t.title}</h3>
                <p className="text-[16px] leading-[2.1] text-gray-900/70">{t.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Courses. Each card is a single anchor rather than a card containing a
          link: one tab stop, and the whole surface is the target on a phone. */}
      <section id="courses" className="bg-white py-16 md:py-20 scroll-mt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="mb-4 text-[28px] font-bold leading-[1.6] text-brand-navy md:text-[34px]">
            دوره‌های ویدیویی
          </h2>
          <p className="mb-10 max-w-3xl text-[17px] leading-[2.2] text-gray-900/70">
            هشت دوره‌ی ویدیویی فارسی روی مکتب‌خونه، درباره‌ی جاوااسکریپت، ری‌اکت، نود و کیفیت کد. هر
            دوره روی صفحه‌ی خودش باز می‌شود.
          </p>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {courses.map((c) => (
              <a
                key={c.slug}
                href={courseUrl(c.slug)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analytics.courseClick({ slug: c.slug, src: currentSource() })}
                className="flex flex-col rounded-smooth-lg border border-border-subtle bg-gray-50 p-6 transition-colors duration-150 hover:border-border-strong"
              >
                <div className="mb-3 flex items-start justify-between gap-3">
                  <h3 className="text-[19px] font-bold leading-[1.8] text-brand-navy">{c.title}</h3>
                  {c.free && (
                    <span className="mt-1 shrink-0 rounded-smooth bg-brand-cream px-2.5 py-1 text-[12px] font-bold text-brand-forest">
                      رایگان
                    </span>
                  )}
                </div>
                <p className="mb-5 flex-1 text-[16px] leading-[2.1] text-gray-900/70">{c.blurb}</p>
                <div className="flex items-center gap-4 border-t border-border-subtle pt-4 text-[14px] text-gray-900/55">
                  <span>{faCount(c.students)} دانشجو</span>
                  <span aria-hidden="true">·</span>
                  <span>
                    امتیاز {faRating(c.rating)} از {faNum(5)}
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="bg-brand-cream py-16 md:py-20 scroll-mt-4">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="mb-6 text-[28px] font-bold leading-[1.6] text-brand-navy md:text-[34px]">
            درباره‌ی من
          </h2>
          <p className="mb-5 text-[17px] leading-[2.2] text-gray-900/75">
            احسان گازار هستم، مهندس نرم‌افزار Staff ساکن لندن. بیش از {faNum(15)} سال است که در
            دنیای توسعه‌ی نرم‌افزار فعالیت می‌کنم؛ از طراحی رابط کاربری تا معماری سیستم‌های پیچیده‌ای
            که باید سال‌ها بدون وقفه زیر بار ترافیک واقعی پایدار بمانند.
          </p>
          <p className="text-[17px] leading-[2.2] text-gray-900/75">
            با وجود اینکه سال‌هاست به زبان انگلیسی می‌نویسم و تدریس می‌کنم، جای خالی انتقال این
            تجربه‌ها به زبان مادری‌ام را همیشه حس می‌کردم. اینجا نقطه‌ی شروعی برای اشتراک‌گذاری همان
            مباحث با زبان شیرین فارسی است.
          </p>
        </div>
      </section>

      <NewsletterSignup />
    </Shell>
  );
}
